import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import {
    Autocomplete,
    TextField,
    Box,
    Avatar,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { Info as InfoIcon, FiberManualRecord as BulletIcon } from '@mui/icons-material';
import { SCUIButton } from '../../../../atomic/atoms';

import { useStorePickupPackageContext } from '../../context/StorePickupPackageContext';

import airdropShipment from '../../../../assets/icons/svg/pageIcons/airdropShipment.svg';
import StorePickupPackageQuery from '../../../../services/query/storePickupPackage/StorePickupPackageQuery.services';
import { SPP } from '../../storePickupPackage.classes';

export enum PriorityCargoCompanyIds {
    Aras = 1,
    HepsiJet = 40,
    Kargoist = 58,
    Scotty = 86,
    Surat = 78,
    Yurtici = 35,
}

const priorityIds = Object.values(PriorityCargoCompanyIds);

export const CargoSelection = () => {
    const [cargoCompanies, setCargoCompanies] = useState<any[]>([]);
    const [dPackageNumber, setDPackageNumber] = useState('');

    const { t } = useTranslation();
    const {
        setIsCargoCompanySelected,
        setCargoCompany,
        cargoCompany,
        setIsLoading,
        setDPackageCargoShipmentCode,
    } = useStorePickupPackageContext();

    const reorderedCargoCompanies = useMemo(() => {
        if (!cargoCompanies) return [];

        const allOption = {
            id: 'all',
            shippingCompanyId: 'all',
            name: t('storePickupPackageAdmission.allCargoCompanies'),
        };

        const priority = cargoCompanies.filter((company) => priorityIds.includes(company.shippingCompanyId));

        const rest = cargoCompanies.filter((company) => !priorityIds.includes(company.shippingCompanyId));

        // Sort priority companies by the order in `priorityIds`
        priority.sort((a, b) => priorityIds.indexOf(a.shippingCompanyId) - priorityIds.indexOf(b.shippingCompanyId));

        return [allOption, ...priority, ...rest];
    }, [cargoCompanies, t]);

    const handleSelectCargoCompany = (_: any, newValue: any) => {
        setCargoCompany(newValue?.id);
    };

    const handleSearch = () => {
        if (!dPackageNumber && !cargoCompany) {
            Swal.fire(t('storePickupPackageAdmission.selectCargoCompany'), '', 'error');
            return;
        }

        if (dPackageNumber) {
            setDPackageCargoShipmentCode(dPackageNumber);
        } else {
            setDPackageCargoShipmentCode('');
        }

        setIsCargoCompanySelected(true);
    };

    const handleDPackageNumberChange = (value: string) => {
        setDPackageNumber(value.replace(/\D/g, ''));
    };

    useEffect(() => {
        setIsLoading(true);
        StorePickupPackageQuery.StorePickupShippingCompanies()
            .then((res: any) => {
                setIsLoading(false);
                setCargoCompanies(res.data);
                setCargoCompany('all');
            })
            .catch(() => {
                setIsLoading(false);
                Swal.fire(t('general.anErrorOccured', '', 'error'));
            });
    }, []);

    return (
        <Box
            className={SPP.selection}
            sx={{
                padding: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '24px',
            }}
        >
            <Card
                className={SPP.selectionInfoCard}
                sx={{
                    maxWidth: 600,
                    width: '100%',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderLeft: '4px solid #2196f3',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
            >
                <CardContent className={SPP.selectionInfoCardContent} sx={{ padding: '16px 24px' }}>
                    <Box className={SPP.selectionInfoRow} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <InfoIcon
                            className={SPP.selectionInfoIcon}
                            sx={{ color: '#2196f3', marginTop: '2px', fontSize: 20 }}
                        />
                        <Box className={SPP.selectionInfoBody} sx={{ flex: 1 }}>
                            <p
                                className={SPP.selectionInfoTitle}
                                style={{
                                    color: '#2196f3',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    marginBottom: '8px',
                                    margin: '0 0 8px 0',
                                }}
                            >
                                {t('storePickupPackageAdmission.informationTitle')}
                            </p>
                            <List className={SPP.selectionInfoList} sx={{ padding: 0, margin: 0 }}>
                                <ListItem
                                    className={`${SPP.selectionInfoItem} ${SPP.selectionInfoItemWaiting}`}
                                    sx={{ padding: '2px 0', alignItems: 'flex-start' }}
                                >
                                    <ListItemIcon sx={{ minWidth: '20px', marginTop: '6px' }}>
                                        <BulletIcon sx={{ fontSize: 8, color: '#666' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t('storePickupPackageAdmission.waitingStatusInfo')}
                                        sx={{
                                            margin: 0,
                                            '& .MuiListItemText-primary': {
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                color: '#666',
                                            },
                                        }}
                                    />
                                </ListItem>
                                <ListItem
                                    className={`${SPP.selectionInfoItem} ${SPP.selectionInfoItemPackage}`}
                                    sx={{ padding: '2px 0', alignItems: 'flex-start' }}
                                >
                                    <ListItemIcon sx={{ minWidth: '20px', marginTop: '6px' }}>
                                        <BulletIcon sx={{ fontSize: 8, color: '#666' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t('storePickupPackageAdmission.packageStatusInfo')}
                                        sx={{
                                            margin: 0,
                                            '& .MuiListItemText-primary': {
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                color: '#666',
                                            },
                                        }}
                                    />
                                </ListItem>
                                <ListItem
                                    className={`${SPP.selectionInfoItem} ${SPP.selectionInfoItemDPackage}`}
                                    sx={{ padding: '2px 0', alignItems: 'flex-start' }}
                                >
                                    <ListItemIcon sx={{ minWidth: '20px', marginTop: '6px' }}>
                                        <BulletIcon sx={{ fontSize: 8, color: '#666' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t('storePickupPackageAdmission.dPackageListingInfo')}
                                        sx={{
                                            margin: 0,
                                            '& .MuiListItemText-primary': {
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                color: '#666',
                                            },
                                        }}
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            <Avatar
                className={SPP.selectionAvatar}
                sx={{ width: '200px', height: '200px', borderRadius: '0' }}
                alt="#"
                src={airdropShipment}
            />
            <p
                className={SPP.selectionNoSearchText}
                style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '24px',
                    textAlign: 'center',
                    color: '#666',
                    margin: 0,
                }}
            >
                {t('storePickupPackageAdmission.noSearch')}
            </p>
            <Autocomplete
                disablePortal
                disabled={!!dPackageNumber}
                className={`${SPP.selectionCargoAutocomplete} ${SPP.inputCargoCompany}`}
                sx={{
                    width: '250px',
                    '& .MuiInputBase-root': {
                        padding: '2px 0 2px 8px !important',
                    },
                }}
                options={reorderedCargoCompanies}
                getOptionLabel={(option: any) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={reorderedCargoCompanies.find((company) => company.id === cargoCompany) || null}
                onChange={handleSelectCargoCompany}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} data-cargo-company-id={option.id}>
                        {option.name}
                    </li>
                )}
                renderInput={(params: any) => (
                    <TextField {...params} label={t('storePickupPackageAdmission.selectCargoCompany')} />
                )}
            />
            <TextField
                label={t('storePickupPackageAdmission.dPackageNumber')}
                className={SPP.inputDPackageNumber}
                sx={{
                    width: '250px',
                    '& .MuiInputBase-input': {
                        textAlign: 'left',
                    },
                }}
                value={dPackageNumber}
                onChange={(e) => handleDPackageNumberChange(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                        handleSearch();
                    }
                }}
                slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
            />
            <SCUIButton
                text={t('storePickupPackageAdmission.newCargoAdmission')}
                style={{ backgroundColor: 'rgb(0, 123, 255)' }}
                type="primary"
                sizeType="fixed"
                size={'m'}
                onClick={handleSearch}
                className={SPP.btnNewAdmission}
            />
        </Box>
    );
};
